import get from 'lodash/get';
import pick from 'lodash/pick';
import {useState} from 'react';

import Stack from '@mui/material/Stack';
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";

import * as formValidations from '../../helpers/validations';
import AddressField from '../common/AddressField';
import CustomSelect from '../common/CustomSelect';
import CustomTextField from '../common/CustomTextField';
import getPronouns from '../../helpers/getPronouns';
import TitleDivider from "./TitleDivider";

const pronounOptions = getPronouns();

const defaultFormValues = {
  phone: '',
  city: '',
  zip: '',
  addressLine1: '',
  addressLine2: '',
  state: '',
  pronouns: '',
};

const validations = {
  phone: formValidations.phone,
  addressLine1: str => !!str,
  addressLine2: str => true,
  city: formValidations.city,
  zip: formValidations.zip,
  state: state => !!state,
  pronouns: data => true,
};

const maskPhone = numStr => {
  if (numStr === '') return '';
  const result = numStr.split('').reduce((acc, next, i) => {
    if (i > -1 && i < 2) return `${acc}${next}`;
    if(i === 2) return `${acc}${next}) `;
    if (i >= 3 && i < 5) return `${acc}${next}`;
    if (i === 5) return `${acc}${next}-`;
    if (i > 9) return acc;
    return `${acc}${next}`;
  }, '(');
  return result;
};

const unmaskPhone = formattedNumStr => (
  formattedNumStr.replace(/[\(\)\s\-]/g, '')
);

const handleFieldUpdate = (formValues, setFormValues) => fieldName => e => {
  let value;
  if (fieldName === 'phone') value = unmaskPhone(get(e, 'target.value', ''));
  if (fieldName === 'pronouns') value = get(e, 'target.value') || '';
  if (!value && value !== '') {
    value = (get(e, 'target.value', '') || '').replace(/^\s/g, '').replace(/\s$/g, '');
  }
  const values = {
    ...formValues,
    [fieldName]: value,
  };
  return setFormValues(values);
};

const checkIfFormIsValid = (formValues, validationDict) => {
  const containsInvalidValues = Object.keys(validationDict).reduce((acc, k) => (
    validationDict[k] ? acc : false
  ), true);
  return containsInvalidValues;
};

const ContactInfo = props => {
  const {selectedProvider,sessionType,onSubmit,user,date,time} = props;
  const priorValues = (
    user && user.phone
    ? {
      phone: user.phone,
      city: user.address_city,
      zip: user.address_zip,
      addressLine1: user.address_line_1,
      addressLine2: user.address_line_2,
      state: user.address_state,
      pronouns: user.personal_pronouns,
    }
    : null
  );
  const [formValues, setFormValues] = useState(
    priorValues ||
    defaultFormValues
  );
  const [blurred, setBlurred] = useState({});
  const onChangeHandler = handleFieldUpdate(formValues, setFormValues);
  const validationDict = Object.keys(validations).reduce((acc, k) => ({
    ...acc,
    [k]: validations[k](formValues[k])
  }), {});
  const formIsValid = checkIfFormIsValid(
    formValues,
    validationDict
  );
  return (
    <Stack gap='16px'>
      <TitleDivider text='Contact Information' />
      <Stack
        flexDirection='column'
        justifyContent='flex-start'
        marginTop='4px !important'
      >
      <Typography marginTop={1} marginBottom={1}>
      To finish booking, please complete the last few steps.
      </Typography>
      <AddressField
        required={true}
        defaultValue={
          user?.address_line_1 
          ? pick(user, 
            'address_line_1', 'address_line_2', 'address_city', 'address_state', 'address_zip',
            'lat', 'lon',
          )
          : null
        }
        showLabel={true}
        onChange={address => {
          setFormValues({
            ...formValues, 
            addressLine1: address.address_line_1,
            addressLine2: address.address_line_2,
            city: address.address_city,
            state: address.address_state,
            zip: address.address_zip,
            lat: address.lat,
            lon: address.lon,
          });
        }}
      />
        <Stack
          flexDirection="row"
          gap={{xs: 2, xsm: 4.5}}
          marginBottom='19.5px'
          flexWrap={{xs: 'wrap', xsm: 'nowrap'}}
          justifyContent='flex-start'
          alignItems='flex-start'
        >
          <CustomTextField
            id="phone"
            label="Phone Number"
            placeholder="(123) 456-7890"
            variant="outlined"
            type="tel"
            required
            fullWidth
            defaultValue={get(user, 'phone')}
            value={maskPhone(formValues.phone || '')}
            error={!validationDict.phone && (blurred.phone || priorValues)}
            onChange={onChangeHandler('phone')}
            onBlur={() => setBlurred({ ...blurred, phone: true })}
          />

          <CustomSelect
            id="pronouns"
            label="Pronouns"
            fullWidth
            defaultValue={get(user, 'personal_pronouns')}
            onChange={onChangeHandler('pronouns')}
            options={pronounOptions}
          />
        </Stack>
        <Button
          sx={{alignSelf: 'center', marginTop: '9px'}}
          variant="primaryGradient"
          size='medium'
          fullWidth={false}
          disabled={!formIsValid}
          onClick={() => onSubmit(formValues)}
        >
          Continue To Consent Forms
        </Button>
      </Stack>
    </Stack>
  );
};

export default ContactInfo;
